import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { Location } from '@reach/router';
import { Helmet } from 'react-helmet';
import { gql } from '@yi/core';

import { graphql, useStaticQuery } from 'gatsby';
import { useAccessTier } from '~/src/common/subscription';
import { useCurrentUser } from '~/src/common/user';

import { ChildrenContainer } from './elements';
import { Footer } from './Footer';
import { Header } from './Header';
import { PageViewData, useSendPageViewAnalytics } from './useSendPageViewAnalytics';
import { useHasMounted, useEnsureGaiaAccountExists, useFeatureFlag } from '../hooks';
import { useLocale } from '../LocaleProvider';
import { SaleBanner } from '../SaleBanner';

type ScriptProps = React.HTMLProps<HTMLScriptElement>;
export type HeaderType = 'simpleNav' | 'simpleNavWithSignIn' | 'hideDefaultSideNav' | 'hideTopNav';

interface Props {
  pageTitle: string;
  pageViewData?: PageViewData;
  scripts?: ScriptProps[];
  headerType?: HeaderType;
  footerType?: 'simpleFooter' | 'hideFooter' | 'hideFooterMobile';
  seo?: {
    title?: string;
    full_url?: string;
    description: string;
    image_url?: string;
  };
}
type StaticQueryData = { data: gql.typesSsr.Query_SiteVars_MainLayout };

const siteTitle = 'Yoga International';

const styles = {
  container: css({
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  }),

  body: css({
    flex: '1',
  }),
};

export const MainLayout_: React.FC<PropsWithChildren<Props & StaticQueryData>> = ({
  children,
  pageTitle,
  pageViewData,
  scripts = [],
  headerType,
  footerType,
  seo,
  data: { yi },
}) => {
  const { SiteVars } = yi;
  const { currentUser, loading } = useCurrentUser({ forceRefresh: true });
  const { tier, loading: loadingTier } = useAccessTier();
  const { locale } = useLocale();

  useSendPageViewAnalytics({ loading: loading || loadingTier, currentUser, pageViewData, tier });

  useFeatureFlag('adyen_paypal'); // cache warming
  useFeatureFlag('redirect_to_change_plan'); // cache warming
  useFeatureFlag('entitlements'); // cache warming
  useEnsureGaiaAccountExists();

  const GATSBY_NOINDEX: string | undefined = process.env.GATSBY_NOINDEX;
  const { hasMounted } = useHasMounted();

  return (
    <Location>
      {({ location: { pathname } }) => {
        const fullUrl = `https://yogainternational.com${pathname}`;
        return (
          <>
            <Helmet title={`${pageTitle} | ${siteTitle}`}>
              {seo && <meta property="og:url" content={seo.full_url || fullUrl} />}
              {seo && <meta property="og:title" content={seo.title || pageTitle} />}
              {seo && (
                <meta
                  property="og:image"
                  content={seo.image_url || SiteVars.hp_logged_out_image?.processed_url || ''}
                />
              )}
              {seo && <meta property="og:description" content={seo.description} />}

              {seo?.title && <meta name="title" content={seo.title} />}
              <meta name="description" content={seo?.description} />
              <link rel="canonical" href={seo?.full_url || fullUrl} />

              <html lang={locale} />

              {scripts.map(scriptProps => (
                <script
                  defer
                  type="text/javascript"
                  key={scriptProps.src}
                  src={scriptProps.src}
                  {...scriptProps}
                />
              ))}
              <meta name="p:domain_verify" content="ab28c53c1f73437284b55f6293d8f6dd" />
              {GATSBY_NOINDEX && <meta name="robots" content="noindex" />}
            </Helmet>
            <div css={styles.container}>
              <Header kind={headerType} />
              <div css={styles.body}>
                <ChildrenContainer
                  data-testid={hasMounted ? 'mounted-main-layout' : ''}
                  headerType={headerType}
                >
                  <SaleBanner />
                  {children}
                </ChildrenContainer>
              </div>
              <Footer kind={footerType} />
            </div>
          </>
        );
      }}
    </Location>
  );
};

export const MainLayout: React.FC<PropsWithChildren<Props>> = props => {
  const data = useStaticQuery<gql.typesSsr.Query_SiteVars_MainLayout>(graphql`
    query Query_SiteVars_MainLayout {
      yi {
        SiteVars(language: ENGLISH) {
          hp_logged_out_image(params: { width: 1800, format: webp }) {
            processed_url
          }
        }
      }
    }
  `);
  return <MainLayout_ data={data} {...props} />;
};
